// Orchestrates the display of messages and the message input area
import React, { useEffect, useState, useCallback } from 'react';
import { useChatContext } from '@/contexts/ChatContext';
import { ChatMessage } from './ChatMessage';
import { MessageInput } from './MessageInput';
import { Loader2, UploadCloud, Sparkles } from 'lucide-react';
import { useScroll } from '../hooks/use-scroll';
import { useTranslation } from 'react-i18next';
import { QuickActionButtons } from './QuickActionButtons';
import { ChatCanvas } from './ChatCanvas';
import { MessageCitation } from '@/types/chat';
import { useCitationStore } from '@/stores/citationStore';
import { normalizeUrl } from '@/lib/urlNormalize';
import { useMessageCitations } from '@/hooks/useMessageCitations';
import { debugLog } from '@/lib/debug';

interface ChatContentProps {
  className?: string;
  isMobile?: boolean;
}

export const ChatContent: React.FC<ChatContentProps> = ({ className, isMobile }) => {
  const {
    activeChat,
    isLoading,
    isResponding,
    isUploading,
    messageVisibility,
    messageHoverStates,
    handleFileDrop,
    sendMessage,
    openLastConversation,
    openSidebar,
  } = useChatContext();
  const { t } = useTranslation();
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isPointerInsideChatWindow, setIsPointerInsideChatWindow] = useState(false);
  const [lastHoveredMessageId, setLastHoveredMessageId] = useState<string | null>(null);

  // Use citation store for unified state management
  const {
    selectedMessageCitations,
    autoOpenForMessage,
    showCitations,
    clearCitations,
    closeCanvas,
    setSelectedUrl,
  } = useCitationStore();

  const hoverPreviewPreviousCitationsRef = React.useRef<MessageCitation[] | null>(null);
  const isHoverPreviewingRef = React.useRef(false);
  const hoverPreviewMessageIdRef = React.useRef<string | null>(null);
  const selectedCitationsRef = React.useRef<MessageCitation[] | null>(null);

  useEffect(() => {
    selectedCitationsRef.current = selectedMessageCitations;
  }, [selectedMessageCitations]);

  // Computed canvas open state
  const canvasOpen = selectedMessageCitations !== null && selectedMessageCitations.length > 0;

  const {
    scrollToBottom,
    messagesStartRef,
    messagesEndRef,
    handleScroll,
    scrollToMessage,
  } = useScroll(
    isResponding,
    activeChat?.messages || [],
    activeChat?.id
  );

  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);

  const handleOuterWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const target = event.target as Node;
    if (scrollContainer.contains(target)) {
      return;
    }

    if (target instanceof Element && target.closest('[data-scroll-proxy-blocker="true"]')) {
      return;
    }

    const delta = event.deltaY;
    if (delta === 0) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    const canScrollDown = scrollTop + clientHeight < scrollHeight;
    const canScrollUp = scrollTop > 0;

    const wantsScrollDown = delta > 0;
    const wantsScrollUp = delta < 0;

    const shouldProxy = (wantsScrollDown && canScrollDown) || (wantsScrollUp && canScrollUp);
    if (!shouldProxy) {
      return;
    }

    event.preventDefault();
    scrollContainer.scrollBy({ top: delta, behavior: 'auto' });
  }, []);

  // Clear citations when switching chats or going mobile
  useEffect(() => {
    // Always clear when this effect runs
    // It runs when: activeChat.id changes (conversation switch) OR isMobile changes
    clearCitations();
    hoverPreviewPreviousCitationsRef.current = null;
    hoverPreviewMessageIdRef.current = null;
    isHoverPreviewingRef.current = false;
    selectedCitationsRef.current = null;
    setLastHoveredMessageId(null);
  }, [activeChat?.id, isMobile, clearCitations]);

  // Simplified auto-open detection: track the latest message (any role) and its links
  const lastMessageWithLinksCandidate = React.useMemo(() => {
    if (!activeChat || activeChat.messages.length === 0) return null;
    // Always consider the latest message in the conversation; the citation hook
    // will determine whether it actually has links/citations.
    return activeChat.messages[activeChat.messages.length - 1];
  }, [activeChat?.messages]);

  // Get citations for the latest message using hook (prioritizes API, falls back to extraction)
  const { citations: lastMessageCitations, source: citationSource } = useMessageCitations(lastMessageWithLinksCandidate);

  // Track the currently hovered message (if any)
  const activeHoveredMessageId = React.useMemo(() => {
    if (!messageHoverStates) return null;
    const activeEntry = Object.entries(messageHoverStates).find(([, isHovering]) => isHovering);
    return activeEntry?.[0] ?? null;
  }, [messageHoverStates]);

  React.useEffect(() => {
    if (activeHoveredMessageId) {
      setLastHoveredMessageId(activeHoveredMessageId);
    } else if (!isPointerInsideChatWindow) {
      setLastHoveredMessageId(null);
    }
  }, [activeHoveredMessageId, isPointerInsideChatWindow]);

  const hoveredMessageId = activeHoveredMessageId || (isPointerInsideChatWindow ? lastHoveredMessageId : null);

  const hoveredMessage = React.useMemo(() => {
    if (!hoveredMessageId || !activeChat?.messages) return null;
    return activeChat.messages.find((msg) => msg.id === hoveredMessageId) || null;
  }, [activeChat?.messages, hoveredMessageId]);

  const { citations: hoveredMessageCitations } = useMessageCitations(hoveredMessage);

  // Auto-open canvas for new assistant messages with citations
  useEffect(() => {
    debugLog.canvas('ChatContent useEffect triggered', {
      hasActiveChat: !!activeChat,
      isMobile,
      lastMessageId: lastMessageWithLinksCandidate?.id,
      lastMessageCitationsCount: lastMessageWithLinksCandidate?.citations?.length || 0,
      extractedCitationsCount: lastMessageCitations.length,
      citationSource,
    });

    if (!activeChat || isMobile) {
      debugLog.canvas('Skipping auto-open check (no activeChat or mobile)');
      return;
    }

    const hasCitations = lastMessageCitations.length > 0;

    debugLog.canvas('Auto-open evaluation', {
      messageId: lastMessageWithLinksCandidate?.id,
      hasCitations,
      citationsCount: lastMessageCitations.length,
      citationSource,
      citationsFromMessage: lastMessageWithLinksCandidate?.citations?.length || 0,
    });

    if (hasCitations && lastMessageWithLinksCandidate) {
      debugLog.canvas('Calling autoOpenForMessage from ChatContent', {
        messageId: lastMessageWithLinksCandidate.id,
        citationsCount: lastMessageCitations.length,
        source: citationSource,
      });
      autoOpenForMessage(lastMessageWithLinksCandidate.id, lastMessageCitations);
    }
    // Don't auto-clear if latest message has no citations - let canvas persist
    // Citations from previous messages should remain visible
    // User can manually close canvas or click links to switch
  }, [
    activeChat,
    lastMessageWithLinksCandidate?.id,
    lastMessageCitations,
    citationSource,
    isMobile,
    autoOpenForMessage,
    clearCitations,
    // NOTE: selectedMessageCitations is NOT included as it's an OUTPUT of this effect
  ]);

  // Build citation-to-message map for efficient lookup (memoized)
  const citationToMessageMap = React.useMemo(() => {
    const map = new Map<string, MessageCitation[]>();
    activeChat?.messages.forEach(msg => {
      if (msg.citations && msg.citations.length > 0) {
        msg.citations.forEach(citation => {
          if (citation.id) {
            map.set(citation.id, msg.citations || []);
          }
        });
      }
    });
    return map;
  }, [activeChat?.messages]);

  const handleCitationClick = useCallback((citation: MessageCitation) => {
    if (!citation?.id) return;

    // Find which message this citation belongs to
    const messageCitations = citationToMessageMap.get(citation.id);

    if (messageCitations) {
      // Always show the citations from this message
      // This allows switching between different messages' canvases
      showCitations(messageCitations);
    }

    // Set selectedUrl to persist selection and trigger scrolling
    const normalizedCitationUrl = normalizeUrl(citation.url);
    setSelectedUrl(normalizedCitationUrl);
  }, [citationToMessageMap, setSelectedUrl, showCitations]);

  const handleShowCitationsForMessage = useCallback((citations: MessageCitation[]) => {
    if (citations && citations.length > 0) {
      showCitations(citations);
    } else {
      clearCitations();
    }
  }, [showCitations, clearCitations]);

  // Handle hovered message previews to temporarily switch canvas contents
  useEffect(() => {
    if (isMobile) return;

    const restorePreviousCitations = () => {
      if (!isHoverPreviewingRef.current) return;
      const previous = hoverPreviewPreviousCitationsRef.current;
      if (previous && previous.length > 0) {
        showCitations(previous);
      } else {
        clearCitations();
      }
      hoverPreviewPreviousCitationsRef.current = null;
      hoverPreviewMessageIdRef.current = null;
      isHoverPreviewingRef.current = false;
    };

    const hoveredMessageHasCitations = hoveredMessage && hoveredMessageCitations.length > 0;

    if (hoveredMessageHasCitations) {
      if (!isHoverPreviewingRef.current) {
        hoverPreviewPreviousCitationsRef.current = selectedCitationsRef.current;
        isHoverPreviewingRef.current = true;
      }

      if (hoverPreviewMessageIdRef.current !== hoveredMessage.id) {
        hoverPreviewMessageIdRef.current = hoveredMessage.id;
        showCitations(hoveredMessageCitations);
      }
      return;
    }

    if (!hoveredMessage) {
      // Only restore when cursor leaves messages entirely. Hovering over
      // messages without citations should not toggle the canvas repeatedly.
      restorePreviousCitations();
    }
  }, [
    hoveredMessage,
    hoveredMessageCitations,
    isMobile,
    showCitations,
    clearCitations,
  ]);

  // When selectedMessageCitations is set, use only those (1-to-1 mapping)
  // Otherwise, fallback to all citations from all messages (for manual show citations)
  const rawCanvasItems = selectedMessageCitations || (activeChat?.messages
    .flatMap(message => (message.citations as MessageCitation[]) || []) || []);

  const currentCanvasItems = React.useMemo(() => {
    return rawCanvasItems.filter((citation, index, self) => {
      if (!citation) return false;
      const normalizedUrl = normalizeUrl(citation.url);
      return index === self.findIndex(candidate =>
        candidate && normalizeUrl(candidate.url) === normalizedUrl
      );
    });
  }, [rawCanvasItems]);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      const items = Array.from(e.dataTransfer.items);
      if (items.some(item => item.kind === 'file')) {
        setIsDraggingOver(true);
      }
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const relatedTarget = e.relatedTarget as Node;
    if (!e.currentTarget.contains(relatedTarget) && !document.body.contains(relatedTarget)) {
      setIsDraggingOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDraggingOver && e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      const items = Array.from(e.dataTransfer.items);
      if (items.some(item => item.kind === 'file')) {
        setIsDraggingOver(true);
      }
    }
  }, [isDraggingOver]);

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    if (isResponding || isUploading) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await handleFileDrop(files, activeChat?.difyConversationId || '');
    }
  }, [handleFileDrop, isResponding, isUploading, activeChat?.difyConversationId]);

  const handleQuickAction = useCallback(async (text: string) => {
    if (isResponding) return;
    await sendMessage(text);
  }, [sendMessage, isResponding]);

  const handleOpenLastConversation = useCallback(() => {
    openLastConversation();
    if (isMobile) {
      openSidebar();
    }
  }, [openLastConversation, openSidebar, isMobile]);

  const renderEmptyStateContent = () => (
    <div className={`flex flex-col items-center justify-center text-neutral-500 dark:text-neutral-400 text-center p-4 ${isDraggingOver ? 'opacity-50' : ''}`}>
      <div className="flex items-center justify-center bg-transparent">
        <img src="/images/lema-logo.png" alt="Lema Logo" className="w-24 mb-4" style={{ background: 'none' }} />
      </div>
      <p className="text-xl mb-2">{t('chatbot_description')}</p>
      <p>{t('start_conversation')}</p>
      <QuickActionButtons onSelect={handleQuickAction} onOpenLastConversation={handleOpenLastConversation} />
      <div className="flex-shrink-0">
        <MessageInput />
      </div>
    </div>
  );

  const renderMessages = () => {
    if (!activeChat) return null;
    if (isLoading) {
      return (
        <div className="flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-neutral-500 dark:text-neutral-400" />
        </div>
      );
    }

    if (activeChat.messages.length === 0 && !isResponding) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-neutral-500 dark:text-neutral-400 text-center">
          {renderEmptyStateContent()}
        </div>
      );
    }

    return activeChat.messages.map((message, index) => {
      const isVisible = messageVisibility[message.id] !== false;
      if (!isVisible) return null;

      return (
        <ChatMessage
          key={message.id}
          message={message}
          files={activeChat.files || []}
          isLastAssistantMessage={message.role === 'assistant' && index === activeChat.messages.length - 1}
          scrollToMessage={scrollToMessage}
          onCitationClick={isMobile ? undefined : ((citation) => citation && handleCitationClick(citation))}
          onShowCitations={isMobile ? undefined : handleShowCitationsForMessage}
        />
      );
    });
  };

  // Determine the current state for layout decisions
  const hasActiveChat = !!activeChat;
  const hasMessages = activeChat?.messages && activeChat.messages.length > 0;
  const isEmptyState = !hasActiveChat || (!hasMessages && !isResponding);

  return (
    <div
      className={`flex h-full ${className || ''}`}
      onMouseEnter={() => setIsPointerInsideChatWindow(true)}
      onMouseLeave={() => setIsPointerInsideChatWindow(false)}
      onWheelCapture={handleOuterWheel}
    >
      {/* Main content area */}
      {/* Desktop: Center and constrain width; Mobile: Full width */}
      <div className="flex flex-1 w-full min-w-0">
        <div
          className={`flex flex-col relative w-full ${!isMobile ? 'max-w-3xl mx-auto' : ''} ${isDraggingOver ? 'opacity-50' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {/* Drag overlay */}
          {isDraggingOver && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-neutral-100/80 dark:bg-neutral-800/80 border-2 border-dashed border-neutral-500 dark:border-neutral-400 rounded-lg pointer-events-none">
              <UploadCloud className="h-12 w-12 text-neutral-700 dark:text-neutral-300 mb-2" />
              <p className="text-lg text-neutral-800 dark:text-neutral-200">{t('drop_files_here')}</p>
            </div>
          )}

          {isEmptyState ? (
            // Empty state: No active chat OR active chat with no messages
            <div className="flex flex-col h-full">
              {/* Empty state content - centered */}
              <div className="flex-1 flex flex-col items-center justify-center min-h-0">
                {renderEmptyStateContent()}
              </div>

            </div>
          ) : (
            // Active chat with messages
            <div className="flex flex-col h-full min-h-0">
              {/* Message Display Area - Make this flex-1 so it grows and scrolls */}
              <div
                ref={scrollContainerRef}
                className={`flex-1 overflow-y-auto scrollbar-hide p-4 ${isMobile ? 'pb-24' : 'pb-32'} space-y-4 min-h-0 ${isDraggingOver ? 'opacity-50' : ''}`}
                onScroll={handleScroll}
              >
                <div ref={messagesStartRef} /> {/* Anchor for scrolling up */}

                {/* Show loader if loading messages */}
                {isLoading && activeChat ? (
                  <div className="flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-neutral-500 dark:text-neutral-400" />
                  </div>
                ) : (
                  // Render messages
                  renderMessages()
                )}

                <div ref={messagesEndRef} /> {/* Anchor for scrolling down */}
              </div>

              {/* Message Input - fixed at bottom */}
              <div className="flex-shrink-0 relative z-10">
                <MessageInput />
              </div>
            </div>
          )}
        </div>
      </div>

      {!isMobile && (
        <ChatCanvas
          isOpen={canvasOpen}
          onClose={closeCanvas}
          items={currentCanvasItems}
        />
      )}
    </div>
  );
};
